import { createContext, type ReactNode, useMemo } from 'react'
import type { ContentProps } from './Content.types.ts'

/**
 * This should only contain primitive values to avoid unnecessary re-renders
 */
export type ContentOptionsContextValue = Pick<
  ContentProps,
  | 'className'
  | 'cellClassName'
  | 'labelCellClassName'
  | 'labelClassName'
  | 'labelAlign'
  | 'descriptionCellClassName'
  | 'descriptionClassName'
  | 'descriptionAlign'
  | 'font'
  | 'size'
  | 'direction'
  | 'align'
  | 'shouldWrapLabel'
  | 'shouldWrapDescription'
>

// eslint-disable-next-line react-refresh/only-export-components
export const ContentOptionsContext = createContext<ContentOptionsContextValue>(
  {},
)

export interface ContentOptionsProviderProps
  extends ContentOptionsContextValue {
  children?: ReactNode
}

export const ContentOptionsProvider = (props: ContentOptionsProviderProps) => {
  const options = useMemo(
    () => ({
      className: props.className,
      cellClassName: props.cellClassName,
      labelCellClassName: props.labelCellClassName,
      labelClassName: props.labelClassName,
      labelAlign: props.labelAlign,
      descriptionCellClassName: props.descriptionCellClassName,
      descriptionClassName: props.descriptionClassName,
      descriptionAlign: props.descriptionAlign,
      font: props.font,
      size: props.size,
      direction: props.direction,
      align: props.align,
      shouldWrapLabel: props.shouldWrapLabel,
      shouldWrapDescription: props.shouldWrapDescription,
    }),
    [
      props.align,
      props.cellClassName,
      props.className,
      props.descriptionAlign,
      props.descriptionCellClassName,
      props.descriptionClassName,
      props.direction,
      props.font,
      props.labelAlign,
      props.labelCellClassName,
      props.labelClassName,
      props.shouldWrapDescription,
      props.shouldWrapLabel,
      props.size,
    ],
  )

  return (
    <ContentOptionsContext.Provider value={options}>
      {props.children}
    </ContentOptionsContext.Provider>
  )
}
